import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { useOrdersReportApi } from '@hooks/api';

import config from '../config';

import type { QueryKey } from 'react-query';
import type { OrderSearchRequest } from '@goodfynd/types';
import type { OrdersReportPopularProductsResponse } from '../services/api/types';
type ReportPopularProductsOptions = {
  onError?: (err: unknown) => void;
  onSuccess?: (
    data: OrdersReportPopularProductsResponse,
    toggleBusy: Dispatch<SetStateAction<boolean>>
  ) => void;
  queryKey?: QueryKey;
  refetchInterval?: number;
  refetchOnWindowFocus?: boolean;
  request: OrderSearchRequest;
  updateSearch?: () => void;
};

export function useReportPopularProducts({
  onError,
  onSuccess,
  queryKey = config.queryKeys.ordersReportTrend,
  refetchInterval,
  refetchOnWindowFocus = false,
  request,
}: ReportPopularProductsOptions) {
  const api = useOrdersReportApi();
  const [busy, toggleBusy] = useState(false);

  const { data, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    OrdersReportPopularProductsResponse,
    unknown
  >(
    [queryKey, request],
    async () => {
      return await api.popularProducts(request);
    },
    {
      enabled: !!request,
      onError,
      onSuccess(data) {
        onSuccess?.(data, toggleBusy);
      },
      refetchInterval,
      refetchOnWindowFocus,
    }
  );

  const results = useMemo(
    () => ({
      popularProducts: data?.results || [],
      totalItems: data?.totalItems || 0,
    }),
    [data]
  );

  return {
    ...results,
    isError,
    isFetched,
    isFetching: isFetching || busy,
    isLoading,
    refetch,
  };
}
